import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ButtonDropdown, ConfirmDialog } from 'app/components';
import { TranslationKeys } from 'app/translations';

import type { SynchronizeInsurerOption } from './synchronizeInsurerDataOptions';
import { getSynchronizeInsurerOptions } from './synchronizeInsurerDataOptions';

export const BTN_SYNCHRONIZE_INSURER_DATA_TESTID = 'btn-synchronize-insurer-data';
export const MODAL_SYNCHRONIZE_EMPLOYEE_DATA_TESTID = 'modal-synchronize-employee-data';

type SynchronizeInsurerDataDropdownProps = {
  connectionType?: string | null;
  disabled?: boolean;
  onRun: (option: SynchronizeInsurerOption) => void;
};

export const SynchronizeInsurerDataDropdown = ({
  connectionType,
  disabled = false,
  onRun,
}: SynchronizeInsurerDataDropdownProps) => {
  const intl = useIntl();
  // The selected option is kept after closing so the dialog title does not change during the close transition.
  const [optionPendingConfirmation, setOptionPendingConfirmation] = React.useState<SynchronizeInsurerOption | null>(
    null,
  );
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);

  const synchronizeOptions = React.useMemo(() => getSynchronizeInsurerOptions(connectionType), [connectionType]);

  const handleOptionClick = React.useCallback(
    (option: SynchronizeInsurerOption) => {
      if (option.requiresConfirmation) {
        setOptionPendingConfirmation(option);
        setConfirmDialogOpen(true);
        return;
      }

      onRun(option);
    },
    [onRun],
  );

  const dropdownOptions = React.useMemo(
    () =>
      synchronizeOptions.map(option => ({
        title: intl.formatMessage({ id: option.titleMessageId }),
        onClick: () => handleOptionClick(option),
      })),
    [handleOptionClick, intl, synchronizeOptions],
  );

  const handleConfirm = React.useCallback(() => {
    if (optionPendingConfirmation) {
      onRun(optionPendingConfirmation);
    }
  }, [onRun, optionPendingConfirmation]);

  if (dropdownOptions.length === 0) {
    return null;
  }

  return (
    <>
      <ButtonDropdown
        options={dropdownOptions}
        disabled={disabled}
        placement={'bottom-start'}
        testId={BTN_SYNCHRONIZE_INSURER_DATA_TESTID}
      />
      <ConfirmDialog
        title={optionPendingConfirmation ? intl.formatMessage({ id: optionPendingConfirmation.titleMessageId }) : ''}
        testId={MODAL_SYNCHRONIZE_EMPLOYEE_DATA_TESTID}
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        onConfirm={handleConfirm}
        cancelButtonMessageId={TranslationKeys.button_cancel}
        confirmButtonMessageId={TranslationKeys.button_confirm}
      >
        <FormattedMessage id={TranslationKeys.connections_synchronizeEmployeeData_warning_message} />
      </ConfirmDialog>
    </>
  );
};
